import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PhoneIcon, CheckIcon, ArrowRightIcon, MapPinIcon, ShieldCheckIcon, UserRoundIcon, FileTextIcon, ChevronRightIcon } from 'lucide-react';
import { ServicePageData } from '../../lib/servicePages';
import { FIRM } from '../../lib/constants';
import { Container } from '../ui/Container';
import { SectionHeading } from '../ui/SectionHeading';
import { Button } from '../ui/Button';
import { FAQ } from '../FAQ';
import { EnquiryForm } from '../EnquiryForm';
export function ServicePageTemplate({
  data
}: {
  data: ServicePageData;
}) {
  return <div className="w-full bg-cream" data-id="element-344">
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy text-cream" data-id="element-345">
        <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `url(${data.heroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} aria-hidden data-id="element-346" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/60" aria-hidden data-id="element-347" />
        <Container className="relative py-14 lg:py-20" data-id="element-348">
          <nav className="mb-5 flex items-center gap-1.5 text-xs text-cream/55" aria-label="Breadcrumb" data-id="element-349">
            <Link to="/" className="hover:text-cream" data-id="element-350">
              Home
            </Link>
            <ChevronRightIcon className="h-3 w-3" data-id="element-351" />
            <span className="text-cream/40" data-id="element-352">Services</span>
            <ChevronRightIcon className="h-3 w-3" data-id="element-353" />
            <span className="text-brass-light" data-id="element-354">{data.h1}</span>
          </nav>
          <div className="max-w-3xl" data-id="element-355">
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-cream/15 bg-cream/5 px-3.5 py-1.5 text-xs font-medium text-cream/80" data-id="element-356">
              <MapPinIcon className="h-3.5 w-3.5 text-brass-light" data-id="element-357" /> Nedlands ·
              Perth · Western Australia
            </p>
            <h1 className="font-serif text-4xl font-500 leading-[1.1] tracking-tight sm:text-5xl" data-id="element-358">
              {data.h1}
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-cream/75" data-id="element-359">
              {data.metaLead}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-360">
              <Button href={FIRM.phoneHref} variant="primary" size="lg" data-id="element-361">
                <PhoneIcon className="h-5 w-5" data-id="element-362" /> {data.ctaLabel}
              </Button>
              <Button href="#enquire" variant="outline" size="lg" className="border-cream/25 text-cream hover:border-cream hover:bg-cream/10" data-id="element-363">
                Send a confidential enquiry{' '}
                <ArrowRightIcon className="h-4 w-4" data-id="element-364" />
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-b border-navy/10 bg-white py-14 lg:py-20" data-id="element-365">
        <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12" data-id="element-366">
          <div className="lg:col-span-7" data-id="element-367">
            <SectionHeading eyebrow="Who this is for" title="Is this the right service for you?" data-id="element-368" />
            <p className="mt-5 text-lg leading-relaxed text-ink/70" data-id="element-369">
              {data.forWho}
            </p>
          </div>
          <aside className="lg:col-span-5" data-id="element-370">
            <div className="rounded-xl border border-brass/30 bg-brass/[0.05] p-6" data-id="element-371">
              <div className="flex items-center gap-2" data-id="element-372">
                <MapPinIcon className="h-5 w-5 text-brass-dark" data-id="element-373" />
                <h3 className="font-serif text-lg text-navy" data-id="element-374">
                  Local WA knowledge
                </h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-ink/65" data-id="element-375">
                {data.localNote}
              </p>
            </div>
          </aside>
        </Container>
      </section>

      {/* SITUATIONS */}
      <section className="bg-cream py-14 lg:py-20" data-id="element-376">
        <Container data-id="element-377">
          <SectionHeading eyebrow="How we help" title="Common situations we help with" data-id="element-378" />
          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-379">
            {data.situations.map((s, i) => <motion.li key={s} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: i % 3 * 0.05
          }} className="flex items-center gap-3 rounded-lg border border-navy/10 bg-white px-4 py-3.5" data-id="element-380">
                <CheckIcon className="h-4 w-4 shrink-0 text-brass-dark" data-id="element-381" />
                <span className="text-sm font-medium text-navy" data-id="element-382">{s}</span>
              </motion.li>)}
          </ul>
        </Container>
      </section>

      {/* BEFORE YOU ACT */}
      <section className="bg-white py-14 lg:py-20" data-id="element-383">
        <Container data-id="element-384">
          <SectionHeading eyebrow="Act early" title="What to do before you sign, respond or decide" intro="The most valuable legal advice usually comes before a decision — not after. Here's where to be careful." data-id="element-385" />
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3" data-id="element-386">
            {data.beforeYouAct.map(b => <div key={b.title} className="rounded-xl border border-navy/10 bg-cream p-6" data-id="element-387">
                <h3 className="font-serif text-lg text-navy" data-id="element-388">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/65" data-id="element-389">
                  {b.body}
                </p>
              </div>)}
          </div>
        </Container>
      </section>

      {/* HOW CULLEN MACLEOD HELPS */}
      <section className="bg-navy py-14 text-cream lg:py-20" data-id="element-390">
        <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12" data-id="element-391">
          <div className="lg:col-span-5" data-id="element-392">
            <SectionHeading light eyebrow="Our approach" title="How Cullen Macleod helps" data-id="element-393" />
          </div>
          <ul className="space-y-4 lg:col-span-7" data-id="element-394">
            {data.howWeHelp.map(h => <li key={h} className="flex items-start gap-3" data-id="element-395">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brass/20" data-id="element-396">
                  <CheckIcon className="h-3.5 w-3.5 text-brass-light" data-id="element-397" />
                </span>
                <p className="text-cream/80" data-id="element-398">{h}</p>
              </li>)}
          </ul>
        </Container>
      </section>

      {/* PROCESS */}
      <section className="bg-cream py-14 lg:py-20" data-id="element-399">
        <Container data-id="element-400">
          <SectionHeading eyebrow="Our process" title="A clear, step-by-step approach" data-id="element-401" />
          <ol className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-5" data-id="element-402">
            {data.process.map((p, i) => <li key={p.title} className="rounded-xl border border-navy/10 bg-white p-5" data-id="element-403">
                <span className="font-serif text-2xl font-600 text-brass" data-id="element-404">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="mt-2 h-px w-8 bg-brass/40" data-id="element-405" />
                <h3 className="mt-3 font-serif text-base leading-snug text-navy" data-id="element-406">
                  {p.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink/60" data-id="element-407">
                  {p.body}
                </p>
              </li>)}
          </ol>
        </Container>
      </section>

      {/* MID-PAGE CTA */}
      <section className="bg-white py-8 lg:py-10" data-id="element-408">
        <Container data-id="element-409">
          <div className="flex flex-col items-center justify-between gap-4 rounded-2xl bg-navy p-6 text-cream sm:flex-row sm:p-8" data-id="element-410">
            <div data-id="element-411">
              <h3 className="font-serif text-xl text-cream" data-id="element-412">
                Before you sign, sell, buy or decide — get clear advice.
              </h3>
              <p className="mt-1 text-sm text-cream/70" data-id="element-413">
                A short conversation now can save significant cost later.
              </p>
            </div>
            <div className="flex shrink-0 gap-3" data-id="element-414">
              <Button href={FIRM.phoneHref} variant="primary" size="md" data-id="element-415">
                <PhoneIcon className="h-4 w-4" data-id="element-416" /> {FIRM.phoneDisplay}
              </Button>
              <Button href="#enquire" variant="outline" size="md" className="border-cream/25 text-cream hover:bg-cream/10" data-id="element-417">
                Enquire
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* TEAM */}
      <section className="bg-cream py-14 lg:py-20" data-id="element-418">
        <Container data-id="element-419">
          <SectionHeading eyebrow="Your team" title="Lawyers who handle this work" data-id="element-420" />
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-421">
            {data.team.map(t => <div key={t.name} className="flex items-start gap-4 rounded-xl border border-navy/10 bg-white p-6" data-id="element-422">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-navy/5" data-id="element-423">
                  <UserRoundIcon className="h-7 w-7 text-brass-dark" data-id="element-424" />
                </span>
                <div data-id="element-425">
                  <h3 className="font-serif text-lg text-navy" data-id="element-426">{t.name}</h3>
                  <p className="text-sm font-medium text-brass-dark" data-id="element-427">
                    {t.role}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/60" data-id="element-428">
                    {t.blurb}
                  </p>
                </div>
              </div>)}
          </div>
          <p className="mt-4 text-xs text-ink/45" data-id="element-429">
            Named lawyer profiles available on request — placeholder pending
            confirmation with the firm.
          </p>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14 lg:py-20" data-id="element-430">
        <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12" data-id="element-431">
          <div className="lg:col-span-4" data-id="element-432">
            <SectionHeading eyebrow="FAQs" title="Questions we're often asked" data-id="element-433" />
          </div>
          <div className="lg:col-span-8" data-id="element-434">
            <FAQ items={data.faqs} data-id="element-435" />
          </div>
        </Container>
      </section>

      {/* GUIDES + RELATED */}
      <section className="bg-cream py-14 lg:py-20" data-id="element-436">
        <Container className="grid grid-cols-1 gap-10 lg:grid-cols-2" data-id="element-437">
          <div data-id="element-438">
            <h2 className="font-serif text-2xl text-navy" data-id="element-439">Related guides</h2>
            <ul className="mt-5 space-y-3" data-id="element-440">
              {data.guides.map(g => <li key={g} data-id="element-441">
                  <a href="/#guides" className="group flex items-center gap-3 rounded-lg border border-navy/10 bg-white px-4 py-3.5 transition-colors hover:border-brass/40" data-id="element-442">
                    <FileTextIcon className="h-5 w-5 shrink-0 text-brass-dark" data-id="element-443" />
                    <span className="flex-1 text-sm font-medium text-navy" data-id="element-444">
                      {g}
                    </span>
                    <ArrowRightIcon className="h-4 w-4 text-navy/30 group-hover:text-brass-dark" data-id="element-445" />
                  </a>
                </li>)}
            </ul>
          </div>
          <div data-id="element-446">
            <h2 className="font-serif text-2xl text-navy" data-id="element-447">Related services</h2>
            <ul className="mt-5 space-y-3" data-id="element-448">
              {data.related.map(r => <li key={r.slug} data-id="element-449">
                  <Link to={`/services/${r.slug}`} className="group flex items-center gap-3 rounded-lg border border-navy/10 bg-white px-4 py-3.5 transition-colors hover:border-brass/40" data-id="element-450">
                    <span className="flex-1 text-sm font-medium text-navy" data-id="element-451">
                      {r.label}
                    </span>
                    <ArrowRightIcon className="h-4 w-4 text-navy/30 group-hover:text-brass-dark" data-id="element-452" />
                  </Link>
                </li>)}
            </ul>
          </div>
        </Container>
      </section>

      {/* ENQUIRY FORM + CALL */}
      <section id="enquire" className="scroll-mt-24 bg-navy py-14 text-cream lg:py-20" data-id="element-453">
        <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12" data-id="element-454">
          <div className="lg:col-span-5" data-id="element-455">
            <SectionHeading light eyebrow="Get in touch" title={`Speak with our ${data.team[0].role.toLowerCase()} team`} data-id="element-456" />
            <p className="mt-4 text-cream/70" data-id="element-457">
              Tell us what has happened and we'll help you understand the right
              next step. Confidential, practical, and no obligation.
            </p>
            <div className="mt-6 space-y-3" data-id="element-458">
              <a href={FIRM.phoneHref} className="flex items-center gap-3 rounded-lg border border-cream/15 bg-navy-800 px-4 py-3.5 text-cream transition-colors hover:border-brass" data-id="element-459">
                <PhoneIcon className="h-5 w-5 text-brass-light" data-id="element-460" />
                <span className="font-semibold" data-id="element-461">Call {FIRM.phoneDisplay}</span>
              </a>
              <div className="flex items-start gap-3 rounded-lg border border-cream/10 bg-navy-800 px-4 py-3.5 text-sm text-cream/70" data-id="element-462">
                <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-sage-light" data-id="element-463" />
                Your enquiry is confidential. We will direct it to the most
                appropriate team.
              </div>
            </div>
          </div>
          <div className="lg:col-span-7" data-id="element-464">
            <div className="rounded-2xl bg-cream p-6 sm:p-8" data-id="element-465">
              <EnquiryForm defaultTopic={mapTopic(data.slug)} data-id="element-466" />
            </div>
          </div>
        </Container>
      </section>
    </div>;
}
function mapTopic(slug: string): string | undefined {
  const map: Record<string, string> = {
    'commercial-business-law': 'Business & Commercial',
    'dispute-resolution': 'Disputes & Litigation',
    'property-law': 'Property & Conveyancing',
    conveyancing: 'Property & Conveyancing',
    'family-law': 'Family Law',
    'wills-estates': 'Wills, Estates & Succession',
    franchising: 'Franchising',
    employment: 'Employment & Workplace'
  };
  return map[slug];
}